// Student API helper. Reads JWT from localStorage('desky_token').
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('desky_token') || '';

// Friendly defaults for common HTTP status codes — used both for normal
// error paths AND as a safety net when the response body can't be read
// (e.g. a stale service worker / fetch interceptor that already consumed
// the stream, which would otherwise crash the whole app with a
// "body stream already read" TypeError).
const STATUS_FALLBACK = {
  400: 'Bad request.',
  401: 'Please sign in again to continue.',
  402: 'Not enough Super Credits for this action.',
  403: "You don't have access to this resource.",
  404: 'Not found.',
  409: 'Conflict — please refresh and try again.',
  413: 'That file is too large.',
  422: 'Some inputs are invalid.',
  429: 'Too many requests — please slow down.',
  500: 'Server error. Please try again in a moment.',
  502: 'Upstream service is busy. Please retry.',
  503: 'Service temporarily unavailable.',
  504: 'Request timed out. Please retry.',
};

const request = async (path, opts = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
  };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...opts, headers });

  // Read the body defensively. Clone first so any upstream stream consumer
  // (browser extension, service worker, devtools) can't poison our read.
  // If the read itself still fails, fall back to status-derived metadata
  // instead of bubbling a confusing "body stream already read" TypeError
  // up to React's error overlay.
  let text = '';
  try {
    text = await res.clone().text();
  } catch {
    try { text = await res.text(); } catch { text = ''; }
  }

  let body = null;
  if (text) {
    try { body = JSON.parse(text); }
    catch { body = { _raw: text }; }
  }

  if (!res.ok) {
    const detail =
      (body && (body.detail || body.message || body._raw)) ||
      STATUS_FALLBACK[res.status] ||
      `Request failed (${res.status})`;
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
};

export const api = {
  get: (p) => request(p, { method: 'GET' }),
  post: (p, b) => request(p, { method: 'POST', body: JSON.stringify(b || {}) }),
  put: (p, b) => request(p, { method: 'PUT', body: JSON.stringify(b || {}) }),
  patch: (p, b) => request(p, { method: 'PATCH', body: JSON.stringify(b || {}) }),
  del: (p) => request(p, { method: 'DELETE' }),
  delete: (p) => request(p, { method: 'DELETE' }),
  // Multipart upload — used by the tutor chat attachment composer.
  // We deliberately don't set Content-Type so the browser injects the
  // correct multipart boundary itself.
  upload: async (p, formData) => {
    const headers = {};
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API}${p}`, { method: 'POST', headers, body: formData });
    let text = '';
    try { text = await res.clone().text(); } catch { try { text = await res.text(); } catch { text = ''; } }
    let body = null;
    if (text) { try { body = JSON.parse(text); } catch { body = { _raw: text }; } }
    if (!res.ok) {
      const detail = (body && (body.detail || body.message || body._raw)) || STATUS_FALLBACK[res.status] || `Upload failed (${res.status})`;
      const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      err.status = res.status; err.body = body;
      throw err;
    }
    return body;
  },
};

export const setToken = (t) => {
  if (t) localStorage.setItem('desky_token', t);
};

export const clearToken = () => {
  localStorage.removeItem('desky_token');
  localStorage.removeItem('desky_user');
  localStorage.removeItem('desky_tenant');
};

// Broadcast that a credit-spending (or credit-adding) action just happened so
// "live balance" UI outside the current page — e.g. the credits chip in the
// StudentApp header — can refetch immediately instead of waiting for a reload.
export const notifyCreditsChanged = () => {
  try { window.dispatchEvent(new CustomEvent('desky:credits-changed')); } catch { /* noop */ }
};

export default api;
